/**
 * SDMS Result Validation — workflow action modals and helpers.
 *
 * Scoped to the result-validation kind of <AnalysisListPage>.  Owns the
 * Validate / Rollback / Preview / Open Package modals and the POST calls
 * to /api/v1/analysis/result-validation/{validate,rollback}.
 *
 * Phase 1: list-view parity with prototype.  The backend endpoints stub the
 * DB write and return success; the frontend optimistically updates the rows
 * (status pill → "SDMS Validated" / "Pending SDMS Validation", statusId →
 * DV-1-2 / DV-1-1, dateValidated + validatedByList stamped from the response).
 * Open Package detail view, SDMS spreadsheet popup and Project Notes popup
 * are Phase 2/3 features.
 */
import { useEffect, useRef, useState, type ReactNode } from "react";
import { api } from "../../api/client";
import { qcBatchApi } from "./qcBatch.api";
import { categorizeRow } from "./resultCategory";
import { useSdmsRollbackUi } from "../../utils/sdmsRollbackUi";
import { resetUnsavedTracking } from "../../hooks/useUnsavedGuard";
import "./SdmsRollbackModal.css";

// ── shared row shape (subset of AnalyticalBatchRow used by these actions) ───
export interface SdmsBatch {
  id: string; // SpreadSheetEntry_AnalyticalBatch.Oid
  batchId: string | null; // QCBatchID display
  statusText: string; // 'Pending Validation' | 'SDMS Validated' …
  statusId: string | null; // 'DV-1-1' | 'DV-1-2'
  test: string | null;
  method: string | null;
  templateID: number | null;
  validatedByList: string | null;
  validatedDateMax: string | null;
  // "Submitted By" (Batch Information header) — SpreadSheetEntry_AnalyticalBatch.
  // SamplesSubmittedBy, a distinct field from ValidatedByList (2026-07-17).
  submittedByList: string | null;
  /** "Date Submitted" — ALPACA MAX(sample SubmittedDate), not the batch CreatedDate. */
  submittedDateMax: string | null;
}

// Status the prototype calls "Pending" — the legacy SP returns "Pending Validation".
export const isPending = (r: SdmsBatch) => /pending/i.test(r.statusText) && !/validated/i.test(r.statusText);
export const isValidated = (r: SdmsBatch) => /validated/i.test(r.statusText);

// ── POST helpers ────────────────────────────────────────────────────────────
export interface ValidateResponse {
  success: boolean;
  count: number;
  validatedBy: string;
  validatedAt: string; // ISO
  /** PARTIAL VALIDATION (ALPACA parity): false ⇒ only some grid rows were
   *  validated and the batch stays "Pending SDMS Validation" — the caller must
   *  keep it in the queue and reload the grid rather than dropping it.  True /
   *  undefined ⇒ the batch is fully validated (Status 2→5). */
  fullyValidated?: boolean;
  /** Grid rows newly stamped by this call. */
  stampedSamples?: number;
  /** Grid rows still unvalidated in the batch after this call. */
  remainingSamples?: number;
  /** Per-batch skip reason keyed by batch Oid — populated when the server-side
   *  precondition gate refuses one or more of the requested batches (e.g. a
   *  batch still has unanswered check points). Empty when everything validated
   *  cleanly. Surfaced in the modal so the analyst sees the same message
   *  ALPACA XAF shows on its blue banner. */
  skipped?: Record<string, string>;
  note?: string;
}
export interface RollbackResponse {
  success: boolean;
  count: number;
  rolledBackBy: string;
  rolledBackAt: string;
  /** Per-batch skip reason keyed by batch Oid — populated when the server-side
   *  gate refuses one or more batches (e.g. report already generated).  The
   *  list-view surfaces this in the ALPACA-style red banner across the top of
   *  the page — same wording ALPACA XAF shows. */
  skipped?: Record<string, string>;
  note?: string;
  /** Re-analysis / Re-prep (Enhanced Roll Back): true — only the selected results
   *  left the batch for a queue; the batch itself keeps its status, so the caller
   *  reloads instead of flipping the row to Pending Submission. */
  partial?: boolean;
  /** Results sent back to the queue (partial only). */
  returned?: number;
  /** Related QC / internal-standard / surrogate rows removed (partial only). */
  removed?: number;
  /** "Analysis Queue" | "Sample Prep Queue" (partial only). */
  queue?: string;
}

// ── Review-Log write bus ────────────────────────────────────────────────────
// Any write that can add or change a DataPackageReviewLog row announces itself
// here, and `useReviewLog` re-fetches that batch. It lives at the WRITE helpers
// rather than at each button because there is more than one Validate in the app
// - the package detail's own button and the list-level confirm dialog - and the
// detail's Review Log went stale whenever the click came through a path that
// happened not to call its reload (client 2026-08-19: signed at 17:38:05, the
// grid still showed "Awaiting signature" until a manual F5 at 17:38:45; the API
// log proves no re-fetch was issued in between). Hanging the refresh off the
// POST itself means a new caller cannot forget it.
type ReviewLogWriteListener = (batchOid: string) => void;
const reviewLogWriteListeners = new Set<ReviewLogWriteListener>();

/** Subscribe to review-log-affecting writes. Returns the unsubscribe. */
export function onReviewLogWrite(fn: ReviewLogWriteListener): () => void {
  reviewLogWriteListeners.add(fn);
  return () => {
    reviewLogWriteListeners.delete(fn);
  };
}

function emitReviewLogWrite(...batchOids: (string | undefined)[]): void {
  for (const oid of batchOids) {
    if (!oid) continue;
    for (const fn of reviewLogWriteListeners) fn(oid);
  }
}

export async function postValidate(
  batchIds: string[],
  reason: string,
  sampleInfoIds?: string[],
): Promise<ValidateResponse> {
  const res = await api.post("/api/v1/analysis/result-validation/validate", {
    batchIds,
    reason: reason || null,
    // PARTIAL VALIDATION: the ticked Batch-Results rows
    // (SpreadSheetEntry_SampleInfo.Oid).  Omit/empty ⇒ whole-batch (list-view
    // bulk Validate).  ALPACA parity: DataReviewController.ValidateSamples.
    sampleInfoIds: sampleInfoIds && sampleInfoIds.length > 0 ? sampleInfoIds : null,
  });
  emitReviewLogWrite(...batchIds);
  // The Check Point / Review Log edits were flushed before this call and are now committed, so leaving the
  // screen must not ask "Unsaved changes will be lost" (user report 2026-10-22). Standing rule 15: a form that
  // commits outside useSaveFlow resets the tracker itself.
  resetUnsavedTracking();
  return res.data as ValidateResponse;
}

/** Roll back SDMS batches. Without `partial` it is the whole-batch rollback
 *  (Classic, and the Enhanced "Correction" type). With `partial` it is the Enhanced
 *  Re-analysis / Re-prep of ONE batch: the picked sheet rows
 *  (SpreadSheetEntry_SampleInfo Oids) go back to the Analysis / Sample Prep Queue. */
export async function postRollback(
  batchIds: string[],
  reason: string,
  partial?: { type: "reanalysis" | "reprep"; sampleInfoIds: string[] },
): Promise<RollbackResponse> {
  const res = await api.post("/api/v1/analysis/result-validation/rollback", {
    batchIds,
    reason,
    ...(partial ? { type: partial.type, sampleInfoIds: partial.sampleInfoIds } : {}),
  });
  return res.data as RollbackResponse;
}

// ── Approve (SDMS Result Approval, Level-3) ─────────────────────────────────
export interface ApproveResponse {
  success: boolean;
  count: number;
  approvedBy: string;
  approvedAt: string; // ISO
  /** PARTIAL APPROVAL (ALPACA parity): false ⇒ only some grid rows were
   *  approved and the batch stays in the approval queue (NewStatus still
   *  "Validated"). True / undefined ⇒ fully approved (NewStatus → DV-6-4). */
  fullyApproved?: boolean;
  stampedSamples?: number;
  remainingSamples?: number;
  /** Per-batch skip reason keyed by batch Oid — populated when the server-side
   *  gate refuses one or more batches (already approved, not SDMS Validated). */
  skipped?: Record<string, string>;
  note?: string;
}

export async function postApprove(
  batchIds: string[],
  reason: string,
  sampleInfoIds?: string[],
): Promise<ApproveResponse> {
  const res = await api.post("/api/v1/analysis/sdms-result-approval/approve", {
    batchIds,
    reason: reason || null,
    sampleInfoIds: sampleInfoIds && sampleInfoIds.length > 0 ? sampleInfoIds : null,
  });
  emitReviewLogWrite(...batchIds);
  resetUnsavedTracking();   // committed — see postValidate
  return res.data as ApproveResponse;
}

// ── SDMS package detail inline edits (ALPACA parity) ────────────────────────
// Legacy's batch-edit grid flushes pending Batch Results / Check Points /
// Review Log cell edits to the DB as part of Validate/Approve
// (dviSamples.InnerView.ObjectSpace.CommitChanges(), DataReviewController.cs
// :1971) — BEFORE the status flip. Call this immediately before postValidate /
// postApprove so an edited cell is never silently discarded.
export interface SdmsBatchResultEdit {
  id: string;
  rptLimit?: string | null;
  mcl?: string | null;
  spikeAmount?: string | null;
  dld?: string | null;
  qcStatus?: string | null;
  enteredBy?: string | null;
  enteredDate?: string | null;
}
export interface SdmsCheckPointEdit {
  id: string;
  qualifier?: string | null;
  comment?: string | null;
  decision?: string | null;
}
export interface SdmsReviewLogEdit {
  id: string;
  comment?: string | null;
  decisionStatus?: string | null;
  reviewScope?: string | null;
}
/** The reviewer's decision on the synthesized "SDMS Validation"/"SDMS Approval"
 *  Review Log row (no DB Oid yet). The API UPSERTS a real DataPackageReviewLog
 *  row for it — stamping Operator = the signer (so Role fills via the reader
 *  join), Date, DecisionStatus, ReviewScope and the comment — so it survives
 *  Validate. Legacy XAF creates this row at view-load (DataReviewController). */
export interface SdmsReviewEntry {
  processMode: string; // "SDMS Validation" | "SDMS Approval"
  comment?: string | null;
  decisionStatus?: string | null;
  reviewScope?: string | null;
}
export async function postSaveSdmsEdits(
  batchOid: string,
  edits: {
    batchResults?: SdmsBatchResultEdit[];
    checkPoints?: SdmsCheckPointEdit[];
    reviewLog?: SdmsReviewLogEdit[];
    sdmsReview?: SdmsReviewEntry | null;
  },
): Promise<{ success: boolean; count: number }> {
  const res = await api.post(`/api/v1/analysis/result-validation/${encodeURIComponent(batchOid)}/save-edits`, edits);
  emitReviewLogWrite(batchOid);
  return res.data as { success: boolean; count: number };
}

// ── shared modal scrim + frame ──────────────────────────────────────────────
function Modal({ children, onClose, width }: { children: ReactNode; onClose: () => void; width?: number }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15,18,28,.45)",
        zIndex: 9000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff",
          borderRadius: 12,
          boxShadow: "0 10px 34px rgba(20,24,40,.20)",
          width: Math.min(width ?? 560, 940),
          maxWidth: "94vw",
          overflow: "hidden",
        }}
      >
        {children}
      </div>
    </div>
  );
}

function ModalHead({ title, onClose }: { title: string; onClose: () => void }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "14px 18px",
        borderBottom: "1px solid var(--ai-line)",
      }}
    >
      <span style={{ fontWeight: 700, fontSize: 15 }}>{title}</span>
      <button
        onClick={onClose}
        style={{
          marginLeft: "auto",
          border: "none",
          background: "transparent",
          fontSize: 20,
          color: "var(--ai-muted)",
          cursor: "pointer",
          lineHeight: 1,
        }}
      >
        ×
      </button>
    </div>
  );
}

function ModalFoot({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        display: "flex",
        gap: 8,
        justifyContent: "flex-end",
        padding: "13px 18px",
        borderTop: "1px solid var(--ai-line)",
        background: "var(--ai-header-bg)",
      }}
    >
      {children}
    </div>
  );
}

// ── Validate success popup ─────────────────────────────────────────────────
// ALPACA XAF-style post-save popup: centered dialog with a green tick,
// "Successfully Validated" title, single OK button.  Detail view calls
// postValidate() directly and only renders THIS on success — the
// intermediate "Validate N package(s)?" confirmation is skipped per
// user request (matches the ALPACA flow where the analyst clicks the
// toolbar Validate button and the save fires immediately).
export function ValidateSuccessModal({ count, onClose }: { count: number; onClose: () => void }) {
  return (
    <Modal onClose={onClose} width={440}>
      <div style={{ padding: "26px 22px 14px", textAlign: "center" }}>
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: "50%",
            background: "var(--ai-pill-ok-bg)",
            color: "var(--ai-green)",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 30,
            marginBottom: 12,
          }}
        >
          ✓
        </div>
        <div style={{ fontSize: 16, fontWeight: 700, color: "var(--ai-ink)", marginBottom: 6 }}>
          Successfully Validated
        </div>
        <div style={{ fontSize: 13, color: "var(--ai-muted)", lineHeight: 1.45 }}>
          {count === 1 ? (
            <>
              The selected package has been marked as <b>SDMS Validated</b>.
            </>
          ) : (
            <>
              {count} packages have been marked as <b>SDMS Validated</b>.
            </>
          )}
          <br />
          Status moved <b>DV-1-1 → DV-1-2</b>.
        </div>
      </div>
      <ModalFoot>
        <button
          className="ca-btn ca-btn-solid"
          onClick={onClose}
          autoFocus
          style={{ background: "var(--ai-green)", borderColor: "var(--ai-green)", minWidth: 84 }}
        >
          OK
        </button>
      </ModalFoot>
    </Modal>
  );
}

// ── Validate modal (kept for list-view / bulk paths, unused in the detail
// view which now posts directly and shows only <ValidateSuccessModal>) ─────
// Two phases in one modal:
//   1. Confirmation — "Validate N package(s)?" + reason textarea + Validate button.
//   2. Success — after the POST succeeds, the same modal transforms into an
//      ALPACA-style success popup ("Validated successfully.") with an OK
//      button.  The parent's onConfirmed callback fires when the user clicks
//      OK (not when the POST resolves), so the analyst has to acknowledge the
//      result before the list refreshes — matches legacy XAF's post-save
//      confirmation dialog.
export function ValidateModal({
  targets,
  onClose,
  onConfirmed,
}: {
  targets: SdmsBatch[];
  onClose: () => void;
  onConfirmed: (resp: ValidateResponse, reason: string) => void;
}) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [successResp, setSuccessResp] = useState<ValidateResponse | null>(null);
  const n = targets.length;
  if (n === 0) return null;

  const submit = async () => {
    setBusy(true);
    setErr(null);
    try {
      const resp = await postValidate(
        targets.map((t) => t.id),
        reason.trim(),
      );
      // Server-side gate refused everything → keep the confirm view open and
      // show the same nag ALPACA XAF surfaces on its blue banner.
      const skipped = resp.skipped ?? {};
      const skipCount = Object.keys(skipped).length;
      if (resp.count === 0 && skipCount > 0) {
        setErr(Object.values(skipped).join(" · "));
        return;
      }
      // Success → transform to the ALPACA-parity success popup. onConfirmed
      // will fire when the analyst clicks OK.
      setSuccessResp(resp);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to validate");
    } finally {
      setBusy(false);
    }
  };

  // Phase 2 — success popup (ALPACA-parity: centered dialog with green tick,
  // "Successfully Validated." title, single OK button).
  if (successResp) {
    const ackAndClose = () => {
      onConfirmed(successResp, reason.trim());
      onClose();
    };
    return (
      <Modal onClose={ackAndClose} width={440}>
        <div style={{ padding: "26px 22px 14px", textAlign: "center" }}>
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              background: "var(--ai-pill-ok-bg)",
              color: "var(--ai-green)",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 30,
              marginBottom: 12,
            }}
          >
            ✓
          </div>
          <div style={{ fontSize: 16, fontWeight: 700, color: "var(--ai-ink)", marginBottom: 6 }}>
            Successfully Validated
          </div>
          <div style={{ fontSize: 13, color: "var(--ai-muted)", lineHeight: 1.45 }}>
            {n === 1 ? (
              <>
                The selected package has been marked as <b>SDMS Validated</b>.
              </>
            ) : (
              <>
                {successResp.count} package{successResp.count > 1 ? "s have" : " has"} been marked as{" "}
                <b>SDMS Validated</b>.
              </>
            )}
            <br />
            Status moved <b>DV-1-1 → DV-1-2</b>.
          </div>
        </div>
        <ModalFoot>
          <button
            className="ca-btn ca-btn-solid"
            onClick={ackAndClose}
            autoFocus
            style={{ background: "var(--ai-green)", borderColor: "var(--ai-green)", minWidth: 84 }}
          >
            OK
          </button>
        </ModalFoot>
      </Modal>
    );
  }

  // Phase 1 — confirmation.
  return (
    <Modal onClose={onClose}>
      <ModalHead title={`Validate ${n} package${n > 1 ? "s" : ""}?`} onClose={onClose} />
      <div style={{ padding: 18 }}>
        <p style={{ margin: "0 0 10px", fontSize: 13, color: "var(--ai-ink)" }}>
          Mark the selected package{n > 1 ? "s" : ""} as <b>SDMS Validated</b>. This stamps your analyst code and the
          validation time, and moves the status <b>DV-1-1 → DV-1-2</b>.
        </p>
        <label style={{ fontSize: 12, color: "var(--ai-muted)", display: "block", marginBottom: 4 }}>
          Reason / comment (optional)
        </label>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Reviewed raw runs & QC — within limits."
          disabled={busy}
          style={{
            width: "100%",
            minHeight: 64,
            padding: 8,
            fontSize: 13,
            fontFamily: "inherit",
            resize: "vertical",
            border: "1px solid var(--ai-line)",
            borderRadius: 7,
          }}
        />
        {err && <div style={{ marginTop: 8, color: "var(--ai-red)", fontSize: 12 }}>{err}</div>}
      </div>
      <ModalFoot>
        <button className="ca-btn" onClick={onClose} disabled={busy}>
          Cancel
        </button>
        <button
          className="ca-btn ca-btn-solid"
          onClick={submit}
          disabled={busy}
          style={{ background: "var(--ai-green)", borderColor: "var(--ai-green)" }}
        >
          {busy ? "Validating…" : "Validate"}
        </button>
      </ModalFoot>
    </Modal>
  );
}

// ── Rollback modal (reason REQUIRED) ────────────────────────────────────────
// Ported from the design prototype "SDMS-Validation-Rollback-Prototype-v5":
// a Roll Back TYPE picker (Correction / Re-analysis / Re-prep) above the reason
// box, plus a per-type selection block — Re-prep = a sample checklist, Re-analysis
// = a transposed (parameters-as-rows, searchable · samples-as-columns) matrix that
// scales to large panels (PFAS/VOC, 30–60+ analytes).  The samples + parameters
// are the batch's REAL rows (dbo.SpreadSheetEntry_SampleInfo via the shared
// batch-results endpoint).
//
// WHOLE-SAMPLE for now (owner decision 2026-09-25): the Re-analysis grid keeps its
// layout, but the per-parameter checkboxes are hidden and the cells are read-only —
// a sample is ticked from its column header and ALL its parameters go back together
// (a partly rolled-back sample still showed every parameter when the new QC batch was
// built). Per-parameter Re-analysis is deferred; the SP enforces the same rule.
//
// Behaviour (2026-09-24, "Option A — new batch"): Correction = the unchanged
// whole-batch rollback. Re-analysis / Re-prep send the picked sheet rows to
// dbo.AI_Analysis_Sdms_RollbackPartial: the selected results LEAVE this batch for the
// Analysis / Sample Prep Queue and are re-run in a NEW QC batch; the rest stay
// validated, and the related QC / internal-standard / surrogate rows of the selected
// samples are removed. Re-analysis / Re-prep act on ONE batch (disabled for a bulk
// list rollback).
type RbType = "correction" | "reanalysis" | "reprep";
interface RbTypeDef {
  label: string;
  sub: string;
  route: string;
  sel: "cells" | "samples" | null;
}
const RB_TYPES: Record<RbType, RbTypeDef> = {
  correction: {
    label: "Correction",
    sub: "whole batch → Pending Submission",
    route: "Pending Submission → Correct & Resubmit (SDMS) → re-validate",
    sel: null,
  },
  reanalysis: {
    label: "Re-analysis",
    sub: "Sample × Parameter → Analysis Queue",
    route: "Analysis Queue → SDMS → Submit → re-validate",
    sel: "cells",
  },
  reprep: {
    label: "Re-prep",
    sub: "Sample(s) → Sample Prep Queue",
    route: "Sample Prep Queue → Analysis Queue → SDMS → Submit → re-validate",
    sel: "samples",
  },
};
const RB_TYPE_ORDER: RbType[] = ["correction", "reanalysis", "reprep"];
/** Per-parameter Re-analysis (the prototype's cell / parameter-row ticks). OFF for now —
 *  whole-sample rollback (owner decision 2026-09-25): the ticks stay visible but
 *  read-only. To enable it next phase: set this to true AND set @WholeSample = 0 in
 *  dbo.AI_Analysis_Sdms_RollbackPartial. The selection logic below is kept for it. */
const RB_PER_PARAMETER = false;
const RB_WHOLE_SAMPLE_HINT = "All parameters of a sample are rolled back together — tick the sample above.";
/** Same text the SP throws (50467) when every sample result of the batch is picked. */
const RB_ALL_SELECTED_MSG = "All samples are selected - use Correction to roll back the whole batch.";

interface RbSample {
  name: string;
  site: string | null;
}

// Tri-state checkbox — `indeterminate` isn't a React prop, so set it via ref.
function TriCheckbox({
  checked,
  indeterminate,
  onChange,
  title,
  disabled,
}: {
  checked: boolean;
  indeterminate?: boolean;
  onChange?: (on: boolean) => void;
  title?: string;
  disabled?: boolean;
}) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = !!indeterminate && !checked;
  }, [indeterminate, checked]);
  return (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange?.(e.target.checked)}
      title={title}
      disabled={disabled}
    />
  );
}

export function RollbackModal({
  targets,
  onClose,
  onConfirmed,
}: {
  targets: SdmsBatch[];
  onClose: () => void;
  onConfirmed: (resp: RollbackResponse, reason: string) => void;
}) {
  // Dynamic UI switch (Settings → Default Settings → SDMS Rollback): 'classic' =
  // the legacy reason-only popup; 'enhanced' = the typed roll-back window. Loaded
  // app-wide from /api/v1/app-config after login (AppShell).
  const ui = useSdmsRollbackUi();
  const enhanced = ui === "enhanced";
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [rbType, setRbType] = useState<RbType>("correction");

  // The prototype is per-batch; a single target drives the selection blocks.  A
  // bulk (multi-batch) list rollback shows the type cards but no selection matrix.
  const single = targets.length === 1 ? targets[0] : null;
  const singleId = single?.id ?? null;
  const [samples, setSamples] = useState<RbSample[]>([]);
  const [params, setParams] = useState<string[]>([]);
  const [selLoading, setSelLoading] = useState(false);
  const [selLoaded, setSelLoaded] = useState(false);
  // Sheet-row Oids (SpreadSheetEntry_SampleInfo) behind each matrix cell / sample —
  // what the API needs (it resolves each to its sample + analyte, all runs).
  const [rowIds, setRowIds] = useState<{ cells: Record<string, string[]>; samples: Record<string, string[]> }>(
    { cells: {}, samples: {} });
  const [cells, setCells] = useState<Set<string>>(new Set()); // 'sample||param'
  const [selSamples, setSelSamples] = useState<Set<string>>(new Set());
  const [psearch, setPsearch] = useState("");

  // Fetch the batch's real samples + parameters once (single-batch only), from
  // the same reader the package view uses (SpreadSheetEntry_SampleInfo).  QC
  // controls are skipped — only real samples/analytes are re-prep/re-analyze targets.
  useEffect(() => {
    if (!enhanced || !singleId) return; // classic mode never needs the selection data
    let alive = true;
    setSelLoading(true);
    setSelLoaded(false);
    qcBatchApi
      .sdmsBatchResults(singleId)
      .then((rows) => {
        if (!alive) return;
        const list = rows ?? [];
        // Internal-standard analytes (named by an analyte's ISTD link) follow their
        // sample — never offered as a pick of their own (belt-and-braces with the
        // row flag below, for a batch whose ISTD rows are not flagged).
        const istd = new Set(list.map((r) => (r.internalStandardLinkText ?? "").trim()).filter(Boolean));
        const seen = new Set<string>();
        const smp: RbSample[] = [];
        const pset = new Set<string>();
        const cellIds: Record<string, string[]> = {};
        const sampleIds: Record<string, string[]> = {};
        for (const r of list) {
          // Real client-sample analytes only — the same bucket as the Validation grid's
          // "Sample Results" tab (shared categorizeRow). QC controls, surrogates and
          // internal standards are never shown; the SP removes the related ones from
          // the batch together with the picked samples.
          if (categorizeRow(r) !== "sample") continue;
          // One sample per SampleID, so dilution runs ("…R1") group with their sample.
          const code = (r.sampleId ?? "").trim() || (r.sys ?? "").trim();
          const p = (r.parameter ?? "").trim();
          if (!code || !r.id) continue;
          if (!seen.has(code)) {
            seen.add(code);
            smp.push({ name: code, site: r.siteName ?? r.sampleName ?? null });
          }
          (sampleIds[code] ??= []).push(r.id);
          if (p && !istd.has(p)) {
            pset.add(p);
            (cellIds[code + "||" + p] ??= []).push(r.id);
          }
        }
        setSamples(smp);
        setParams([...pset]);
        setRowIds({ cells: cellIds, samples: sampleIds });
      })
      .catch(() => {
        if (alive) {
          setSamples([]);
          setParams([]);
          setRowIds({ cells: {}, samples: {} });
        }
      })
      .finally(() => {
        if (alive) {
          setSelLoading(false);
          setSelLoaded(true);
        }
      });
    return () => {
      alive = false;
    };
  }, [enhanced, singleId]);

  // Nothing is ticked when a selection type opens (owner decision 2026-09-25 —
  // deviates from the prototype, which pre-ticked a small panel / every Re-prep
  // sample): the analyst ticks only the results / samples that must go back, and
  // "everything" is refused anyway (RB_ALL_SELECTED_MSG).
  useEffect(() => {
    if (!singleId || !selLoaded) return;
    if (rbType === "reanalysis") {
      setPsearch("");
      setCells(new Set());
    } else if (rbType === "reprep") {
      setSelSamples(new Set());
    }
  }, [rbType, selLoaded, singleId]);

  const n = targets.length;
  if (n === 0) return null;

  const def = RB_TYPES[rbType];
  const total = samples.length * params.length;
  const has = (s: string, p: string) => cells.has(s + "||" + p);
  // Prototype selection logic — per cell and per parameter row (used when RB_PER_PARAMETER).
  const setCell = (s: string, p: string, on: boolean) =>
    setCells((prev) => {
      const nx = new Set(prev);
      const k = s + "||" + p;
      if (on) nx.add(k);
      else nx.delete(k);
      return nx;
    });
  const setRow = (p: string, on: boolean) =>
    setCells((prev) => {
      const nx = new Set(prev);
      for (const s of samples) {
        const k = s.name + "||" + p;
        if (on) nx.add(k);
        else nx.delete(k);
      }
      return nx;
    });
  const setCol = (s: string, on: boolean) =>
    setCells((prev) => {
      const nx = new Set(prev);
      for (const p of params) {
        const k = s + "||" + p;
        if (on) nx.add(k);
        else nx.delete(k);
      }
      return nx;
    });
  const setAllCells = (on: boolean) => {
    const nx = new Set<string>();
    if (on) for (const s of samples) for (const p of params) nx.add(s.name + "||" + p);
    setCells(nx);
  };
  const visibleParams = psearch.trim()
    ? params.filter((p) => p.toLowerCase().includes(psearch.trim().toLowerCase()))
    : params;

  const submit = async () => {
    const r = reason.trim();
    if (!r) {
      setErr("A rollback reason is required.");
      return;
    }
    // Re-analysis / Re-prep (Enhanced only): the sheet rows behind the ticked cells /
    // samples. Classic and Correction stay the whole-batch rollback (partialIds null).
    const partialIds = !enhanced || !def.sel
      ? null
      : def.sel === "cells"
        ? [...cells].flatMap((k) => rowIds.cells[k] ?? [])
        : [...selSamples].flatMap((k) => rowIds.samples[k] ?? []);
    if (partialIds) {
      if (!single) {
        setErr("Re-analysis / Re-prep roll back one batch at a time.");
        return;
      }
      if (partialIds.length === 0) {
        setErr(RB_PER_PARAMETER && def.sel === "cells" ? "Select at least one Sample × Parameter cell." : "Select at least one sample.");
        return;
      }
      // Every sample result picked would leave a QC-only batch behind — that is a
      // whole-batch rollback (owner decision 2026-09-25). The SP enforces the same
      // rule (THROW 50467); this is the early, no-round-trip mirror.
      const all = def.sel === "cells"
        ? Object.keys(rowIds.cells).every((k) => cells.has(k))
        : Object.keys(rowIds.samples).every((k) => selSamples.has(k));
      if (all) {
        setErr(RB_ALL_SELECTED_MSG);
        return;
      }
    }
    setBusy(true);
    setErr(null);
    try {
      // The server-side gate may refuse (e.g. report already generated); onConfirmed
      // always fires so the parent surfaces green / red per the response.
      const resp = partialIds && single
        ? await postRollback([single.id], r, { type: rbType as "reanalysis" | "reprep", sampleInfoIds: partialIds })
        : await postRollback(
            targets.map((t) => t.id),
            r,
          );
      // Re-analysis / Re-prep refused by a server guard (no prep step, report already
      // generated, every sample picked …): nothing changed, so keep the popup open with
      // the reason inline — the analyst can switch type or selection and retry.
      const refusal = resp.partial ? Object.values(resp.skipped ?? {})[0] : undefined;
      if (refusal) {
        setErr(refusal);
        return;
      }
      onConfirmed(resp, r);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to roll back");
    } finally {
      setBusy(false);
    }
  };

  // ── CLASSIC (default) — the legacy reason-only "Raw Data Batch Rollback"
  // popup, rendered BYTE-FOR-BYTE as it was before the Enhanced UI was added
  // (title-only header, original label + textarea styling, OK / Cancel). Only
  // the Enhanced mode below is new; `submit` is shared (its selection guard is
  // inert in classic — selLoaded is false, no fetch runs). ──
  if (!enhanced) {
    return (
      <Modal onClose={onClose}>
        {/* ALPACA parity: the legacy "Raw Data Batch Rollback" popup
            (DataReviewController) — a title, a "RollBack Reason:" label + a tall
            reason box, and OK (orange) / Cancel.  Matched 1:1 per the LIMS dialog. */}
        <ModalHead title="Raw Data Batch Rollback" onClose={onClose} />
        <div style={{ padding: 18 }}>
          <label style={{ fontSize: 13, color: "var(--ai-ink)", display: "block", marginBottom: 6 }}>
            RollBack Reason:
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={busy}
            autoFocus
            style={{
              width: "100%",
              minHeight: 120,
              padding: 8,
              fontSize: 13,
              fontFamily: "inherit",
              resize: "vertical",
              border: "1px solid var(--ai-line)",
              borderRadius: 4,
            }}
          />
          {err && <div style={{ marginTop: 8, color: "var(--ai-red)", fontSize: 12 }}>{err}</div>}
        </div>
        <ModalFoot>
          <button
            className="ca-btn ca-btn-solid"
            onClick={submit}
            disabled={busy}
            style={{ background: "var(--brand-accent, #e8670a)", borderColor: "var(--brand-accent, #e8670a)" }}
          >
            {busy ? "Rolling back…" : "OK"}
          </button>
          <button className="ca-btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
        </ModalFoot>
      </Modal>
    );
  }

  const headId = single ? (single.batchId ?? "") : `${n} batches`;

  return (
    <Modal onClose={onClose} width={720}>
      <div className="sdrb">
        {/* Head — title + the batch being rolled back (prototype parity). */}
        <div className="sdrb-hd">
          <b>Raw Data Batch Rollback</b>
          {headId && <span className="sdrb-hint">· {headId}</span>}
          <button className="sdrb-x" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="sdrb-body">
          {/* Roll Back type — ENHANCED mode only.  In CLASSIC mode the modal is the
              legacy reason-only popup (no type cards / selection / routes). */}
          {enhanced && (
            <>
              <div className="sdrb-lbl">
                Roll Back type <span className="sdrb-badge">NEW</span>
              </div>
              <div className="sdrb-types">
                {RB_TYPE_ORDER.map((t) => {
                  // Re-analysis / Re-prep act on ONE batch — off for a bulk list rollback.
                  const off = t !== "correction" && !single;
                  return (
                    <div
                      key={t}
                      className={`sdrb-type ${rbType === t ? "on" : ""} ${off ? "off" : ""}`}
                      onClick={() => !off && setRbType(t)}
                      role="button"
                      tabIndex={off ? -1 : 0}
                      aria-disabled={off}
                      title={off ? "Select a single batch to use Re-analysis / Re-prep." : undefined}
                      onKeyDown={(e) => {
                        if (!off && (e.key === "Enter" || e.key === " ")) setRbType(t);
                      }}
                    >
                      <b>{RB_TYPES[t].label}</b>
                      <small>{RB_TYPES[t].sub}</small>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* Selection block (Re-analysis matrix / Re-prep checklist) */}
          {enhanced && def.sel && (
            <div className="sdrb-selblock">
              <div className="sdrb-lbl">
                <span>
                  {def.sel === "samples"
                    ? "Samples to re-prep:"
                    : `Sample × Parameter to re-analyze (${params.length} params):`}
                </span>
                {single && selLoaded && (samples.length > 0 || params.length > 0) && (
                  <span className="sdrb-mini">
                    <a
                      onClick={() =>
                        def.sel === "samples" ? setSelSamples(new Set(samples.map((s) => s.name))) : setAllCells(true)
                      }
                    >
                      Select all
                    </a>
                    <a onClick={() => (def.sel === "samples" ? setSelSamples(new Set()) : setAllCells(false))}>Clear</a>
                  </span>
                )}
              </div>

              {!single ? (
                <div className="sdrb-selnote">Select a single batch to choose samples / parameters.</div>
              ) : selLoading ? (
                <div className="sdrb-selnote">Loading samples…</div>
              ) : samples.length === 0 ? (
                <div className="sdrb-selnote">No samples found for this batch.</div>
              ) : def.sel === "samples" ? (
                <div className="sdrb-checks">
                  {samples.map((s) => (
                    <label key={s.name}>
                      <input
                        type="checkbox"
                        checked={selSamples.has(s.name)}
                        onChange={(e) =>
                          setSelSamples((prev) => {
                            const nx = new Set(prev);
                            if (e.target.checked) nx.add(s.name);
                            else nx.delete(s.name);
                            return nx;
                          })
                        }
                      />{" "}
                      Sample {s.name} {s.site && <span className="sdrb-site">{s.site}</span>}
                    </label>
                  ))}
                </div>
              ) : (
                <>
                  <div className="sdrb-mxtools">
                    <input
                      placeholder={`Search parameter…  (${params.length} total)`}
                      value={psearch}
                      onChange={(e) => setPsearch(e.target.value)}
                    />
                    <span className="sdrb-mxcount">
                      {cells.size} of {total} selected
                    </span>
                  </div>
                  <div className="sdrb-mxwrap">
                    <table className="sdrb-mx">
                      <thead>
                        <tr>
                          <th className="sdrb-corner">
                            <TriCheckbox
                              checked={total > 0 && cells.size === total}
                              indeterminate={cells.size > 0 && cells.size < total}
                              onChange={setAllCells}
                              title="Select entire batch"
                            />
                          </th>
                          {samples.map((s) => {
                            const all = params.length > 0 && params.every((p) => has(s.name, p));
                            const some = params.some((p) => has(s.name, p));
                            return (
                              <th key={s.name}>
                                <label className="sdrb-col" title={`All parameters · Sample ${s.name}`}>
                                  <TriCheckbox
                                    checked={all}
                                    indeterminate={!all && some}
                                    onChange={(on) => setCol(s.name, on)}
                                  />
                                  {s.name}
                                </label>
                              </th>
                            );
                          })}
                        </tr>
                      </thead>
                      <tbody>
                        {visibleParams.map((p) => {
                          // Parameter-row tick: every / some / no sample. Read-only while
                          // RB_PER_PARAMETER is off (whole-sample rollback).
                          const all = samples.length > 0 && samples.every((s) => has(s.name, p));
                          const some = samples.some((s) => has(s.name, p));
                          return (
                            <tr key={p}>
                              <td className="sdrb-rowhd">
                                <label title={RB_PER_PARAMETER ? `All samples · ${p}` : RB_WHOLE_SAMPLE_HINT}>
                                  <TriCheckbox
                                    checked={all}
                                    indeterminate={!all && some}
                                    onChange={(on) => setRow(p, on)}
                                    disabled={!RB_PER_PARAMETER}
                                  />{" "}
                                  {p}
                                </label>
                              </td>
                              {samples.map((s) => (
                                <td key={s.name}>
                                  {/* Read-only while RB_PER_PARAMETER is off: follows the sample's column tick. */}
                                  <input
                                    type="checkbox"
                                    checked={has(s.name, p)}
                                    onChange={(e) => setCell(s.name, p, e.target.checked)}
                                    disabled={!RB_PER_PARAMETER}
                                    title={RB_PER_PARAMETER ? undefined : RB_WHOLE_SAMPLE_HINT}
                                  />
                                </td>
                              ))}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Reason (required) */}
          <div className="sdrb-lbl">RollBack Reason:</div>
          <textarea
            className="sdrb-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={busy}
            autoFocus
            placeholder="Enter reason…"
          />
          {enhanced && (
            <div className="sdrb-routeline">
              Routes: <b>{def.route}</b>
            </div>
          )}
          {err && <div className="sdrb-err">{err}</div>}
        </div>

        <div className="sdrb-ft">
          <button className="ca-btn ca-btn-solid sdrb-ok" onClick={submit} disabled={busy}>
            {busy ? "Rolling back…" : "OK"}
          </button>
          <button className="ca-btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ── Preview modal (placeholder — full image preview is Phase 2) ─────────────
export function PreviewModal({ target, onClose }: { target: SdmsBatch; onClose: () => void }) {
  return (
    <Modal onClose={onClose} width={580}>
      <ModalHead title={`SDMS Result Image — ${target.batchId ?? "—"}`} onClose={onClose} />
      <div style={{ padding: 18 }}>
        <div
          style={{
            height: 240,
            borderRadius: 10,
            border: "1px dashed var(--ai-line)",
            background:
              "repeating-linear-gradient(45deg,var(--ai-readonly-bg),var(--ai-readonly-bg) 12px,var(--ai-line-soft) 12px,var(--ai-line-soft) 24px)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            color: "var(--ai-muted)",
            fontSize: 13,
          }}
        >
          <i className="ti ti-photo" style={{ fontSize: 36, opacity: 0.55 }} />
          Scanned / instrument result image for {target.test ?? "—"} ({target.method ?? "—"})
          <div style={{ fontSize: 11, marginTop: 6, opacity: 0.8 }}>(Phase 2 — full image preview pending)</div>
        </div>
      </div>
      <ModalFoot>
        <button className="ca-btn" onClick={onClose}>
          Close
        </button>
      </ModalFoot>
    </Modal>
  );
}

// Open Package detail view moved to ./SdmsPackageDetail.tsx (Phase 2 — full
// tabbed Batch Results / Check Points / Review Log / Attachments overlay).

// Per-row Edit clicks open the SAME SdmsPackageDetail overlay as the
// DataPackage "Open Package" link (CLAUDE.md rule 12; legacy parity).  No
// separate inline-row-edit popup — the detail view IS the edit form.
